import { Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { truncate } from 'lodash';
import numeral from 'numeral';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import CustomTable from 'src/components/Table';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryCompanyTradeDataHSCodeList } from 'src/services/queries/useQueryCompanyTradeData';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { KeyFilterChartCTDRelationshipView, KeyFilterChartTradeDataUnitBy } from 'src/utils/constants/enum';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';

interface ITop10HSCodeProps {
  start_date?: string;
  end_date?: string;
  relationshipWithCurrentCompany?: KeyFilterChartCTDRelationshipView;
}

const Top10HSCode = (props: ITop10HSCodeProps) => {
  const { start_date, end_date, relationshipWithCurrentCompany } = props;
  const { keyword } = useQueryParams();
  const { id } = useParams();
  const [params, setParams] = useState({
    offset: 0,
    limit: 10,
    start_date: start_date,
    end_date: end_date,
    company_id: id,
    sort_by: KeyFilterChartTradeDataUnitBy.value_usd,
    order: 'desc',
    is_buyer: relationshipWithCurrentCompany === KeyFilterChartCTDRelationshipView.supplier,
  });

  useEffect(() => {
    setParams({
      ...params,
      start_date: start_date,
      end_date: end_date,
      is_buyer: relationshipWithCurrentCompany === KeyFilterChartCTDRelationshipView.supplier,
    });
  }, [relationshipWithCurrentCompany, start_date, end_date]);

  const { data, isFetching } = useQueryCompanyTradeDataHSCodeList(params);
  const configColumn: ConfigCell<TypeCompanyTradeDataHSCodeList>[] = [
    {
      id: 'hs6_code',
      label: 'HS6 Code',
      disableSort: true,
      cell: (data: TypeCompanyTradeDataHSCodeList) => (
        <Typography variant="body2" color={'text.secondary'}>
          {data.hscode}
        </Typography>
      ),
    },
    {
      id: 'description',
      label: 'Description',
      disableSort: true,
      width: 400,
      cell: (data: TypeCompanyTradeDataHSCodeList) => (
        <Tooltip title={data.description}>
          <Typography variant="body2" color={'text.secondary'}>
            {truncate(data.description, {
              length: 100,
            })}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: 'bol',
      label: 'Bill of Lading',
      cell: (data: TypeCompanyTradeDataHSCodeList) => (
        <Typography variant="body2" color={'text.secondary'}>
          {data.bol}
        </Typography>
      ),
    },
    {
      id: 'weight',
      label: 'Weight (KG)',
      cell: (data: TypeCompanyTradeDataHSCodeList) => (
        <Typography variant="body2" color={'text.secondary'}>
          {numeral(data.weight).format('0,0')}
        </Typography>
      ),
    },
    {
      id: 'quantity',
      label: 'Quantity',
      cell: (data: TypeCompanyTradeDataHSCodeList) => (
        <Typography variant="body2" color={'text.secondary'}>
          {data.quantity}
        </Typography>
      ),
    },
    {
      id: 'value_usd',
      label: 'Value (USD)',
      cell: (data: TypeCompanyTradeDataHSCodeList) => (
        <Typography variant="body2" color={'text.secondary'}>
          {numeral(data.value_usd).format('0,0')}
        </Typography>
      ),
    },
  ];
  return (
    <WhiteBoxContent>
      <Typography mb={3} variant="h5">
        Top 10 HS Code of{' '}
        <Typography display={'inline'} color={'secondary.main'} variant="h5">
          {keyword}
        </Typography>
      </Typography>
      <CustomTable
        handleSort={(e) => {
          setParams({ ...params, sort_by: e.sortBy as KeyFilterChartTradeDataUnitBy, order: e.sort });
        }}
        onChangePage={(value) => {
          setParams({ ...params, offset: value });
        }}
        onChangePageSize={(value) =>
          setParams({
            ...params,
            ...{
              limit: value,
              offset: 0,
            },
          })
        }
        maxRow={10}
        page={params.offset}
        pageSize={params.limit}
        totalRecords={data?.total_records}
        loading={isFetching}
        configColumn={configColumn}
        data={getArrayDefault(data?.data)}
        dataKey="hscode"
      />
    </WhiteBoxContent>
  );
};

export default Top10HSCode;
